import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { ShimmerDiv, showSnackbar, TabPage, TabView } from 'fluxo-ui';
import { SettingsIcon, ShieldIcon, UsersIcon } from 'fluxo-ui/icons';

import { USER_ROLES } from '../common/constants';
import { OrganizationSettings, RoleManagement, UserManagement } from '../components/organization';
import { useAuthStore } from '../store/authStore';
import { useOrganizationStore } from '../store/organizationStore';

const OrganizationPage: React.FC = () => {
    const navigate = useNavigate();
    const { orgId } = useParams<{ orgId?: string }>();
    const { user } = useAuthStore();
    const { organization, users, loading, error, fetchOrganization, updateOrganization, fetchOrganizationUsers } = useOrganizationStore();
    const [initialized, setInitialized] = useState(false);

    // Super admins don't have their own organization - redirect to the organizations
    // list unless they're viewing a specific org by id
    useEffect(() => {
        if (user?.role === USER_ROLES.SUPER_ADMIN && !orgId) {
            navigate('/organizations', { replace: true });
        }
    }, [user, orgId, navigate]);

    useEffect(() => {
        if (user?.role !== USER_ROLES.SUPER_ADMIN || orgId) {
            const loadData = async () => {
                await fetchOrganization(orgId);
                setInitialized(true);
            };
            loadData();
        }
    }, [fetchOrganization, user, orgId]);

    useEffect(() => {
        if (organization) {
            fetchOrganizationUsers();
        }
    }, [organization, fetchOrganizationUsers]);

    useEffect(() => {
        if (error) {
            showSnackbar(error, 'Error', { type: 'error' });
        }
    }, [error]);

    const handleSaveSettings = async (data: any) => {
        try {
            await updateOrganization(data);
            showSnackbar('Organization settings saved', 'Success', { type: 'success' });
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to save settings', 'Error', { type: 'error' });
        }
    };

    if (!initialized || (loading && !organization)) {
        return (
            <div className="p-6 space-y-6">
                <ShimmerDiv level={2} className="h-16 w-full" />
                <ShimmerDiv level={2} className="h-96 w-full" />
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="p-6">
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm text-center py-16 px-6">
                    <UsersIcon className="w-12 h-12 mx-auto text-slate-400 opacity-40" />
                    <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">No Organization Found</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        You are not currently part of any organization. Contact an administrator to join one.
                    </p>
                </div>
            </div>
        );
    }

    const orgSettings = {
        id: organization._id,
        name: organization.name || '',
        domain: (organization as any).domain || '',
        website: organization.website || '',
        description: organization.description || '',
        branding: {
            primaryColor: (organization as any).branding?.primaryColor || '#3B82F6',
            secondaryColor: (organization as any).branding?.secondaryColor || '#8B5CF6',
            logoUrl: organization.logo || '',
        },
        settings: {
            allowPublicSignup: (organization as any).settings?.allowPublicSignup ?? true,
            requireEmailVerification: (organization as any).settings?.requireEmailVerification ?? true,
            allowGuestExams: (organization as any).settings?.allowGuestExams ?? false,
            defaultExamDuration: (organization as any).settings?.defaultExamDuration ?? 60,
            autoApproveQuestions: (organization as any).settings?.autoApproveQuestions ?? false,
            enableAnalytics: (organization as any).settings?.enableAnalytics ?? true,
        },
        subscription: {
            plan: (organization as any).subscription?.plan || 'Free',
            maxUsers: (organization as any).subscription?.maxUsers || 10,
            maxExams: (organization as any).subscription?.maxExams || 50,
            expiresAt: (organization as any).subscription?.expiresAt,
        },
    };

    const mappedUsers = (Array.isArray(users) ? users : []).map((u: any) => ({
        id: u._id || u.id,
        name: u.profile?.name || u.firstName || u.name || u.email || 'Unknown',
        email: u.email || u.emailHash || '',
        role: u.role || 'org_member',
        status: u.status || 'active',
        joinedAt: u.createdAt || u.joinedAt || '',
        lastLogin: u.lastLogin || '',
    }));

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Organization Management</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage your organization settings, users, and roles</p>
            </div>

            <TabView className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
                <TabPage header="Settings" leftIcon={SettingsIcon}>
                    <div className="p-6">
                        <OrganizationSettings organization={orgSettings} onSave={handleSaveSettings} />
                    </div>
                </TabPage>

                <TabPage header="Users" leftIcon={UsersIcon}>
                    <div className="p-6">
                        <UserManagement organizationId={organization._id} users={mappedUsers} />
                    </div>
                </TabPage>

                <TabPage header="Roles & Permissions" leftIcon={ShieldIcon}>
                    <div className="p-6">
                        <RoleManagement
                            userCountsByRole={mappedUsers.reduce(
                                (acc, u) => {
                                    acc[u.role] = (acc[u.role] || 0) + 1;
                                    return acc;
                                },
                                {} as Record<string, number>,
                            )}
                        />
                    </div>
                </TabPage>
            </TabView>
        </div>
    );
};

export default OrganizationPage;
